/**
 * Sound Manager — Web Audio API synthesizer
 * Retro arcade / cyberpunk theme
 *
 * Design principles:
 * - Lazy AudioContext: created only after first user gesture (mobile-safe)
 * - All sounds are synthesized (no external files)
 * - Soft, non-intrusive volumes — game sounds should enhance, not annoy
 * - Preference persisted in localStorage
 */

const STORAGE_KEY = 'lns_sound_enabled'

class SoundManager {
  constructor() {
    this._ctx = null
    this._masterGain = null
    // Read saved preference; default ON
    const saved = localStorage.getItem(STORAGE_KEY)
    this.enabled = saved === null ? true : saved === 'true'
    this.volume = 0.35
  }

  // Lazy init — safe on mobile (must be called inside a user-gesture handler)
  _getCtx() {
    if (!this._ctx) {
      try {
        this._ctx = new (window.AudioContext || window.webkitAudioContext)()
        this._masterGain = this._ctx.createGain()
        this._masterGain.connect(this._ctx.destination)
        this._masterGain.gain.value = this.volume
      } catch {
        return null
      }
    }
    // Resume if suspended (browser policy after inactivity)
    if (this._ctx.state === 'suspended') {
      this._ctx.resume().catch(() => {})
    }
    return this._ctx
  }

  _master() {
    return this._masterGain
  }

  // ─── helpers ───────────────────────────────────────────────

  _osc(ctx, type, freq, startTime, duration, peakGain = 0.4, dest = null) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, startTime)
    gain.gain.setValueAtTime(peakGain, startTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)
    osc.connect(gain)
    gain.connect(dest || this._master())
    osc.start(startTime)
    osc.stop(startTime + duration + 0.01)
    return { osc, gain }
  }

  _sweep(ctx, type, freqStart, freqEnd, startTime, duration, peakGain = 0.4, dest = null) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freqStart, startTime)
    osc.frequency.exponentialRampToValueAtTime(freqEnd, startTime + duration)
    gain.gain.setValueAtTime(peakGain, startTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)
    osc.connect(gain)
    gain.connect(dest || this._master())
    osc.start(startTime)
    osc.stop(startTime + duration + 0.01)
    return { osc, gain }
  }

  // ─── PUBLIC SOUNDS ─────────────────────────────────────────

  /**
   * Button / UI click — short, crisp retro blip
   * Used: any generic button press
   */
  playClick() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'square', 480, 320, t, 0.06, 0.18)
  }

  /**
   * Number hover — very subtle tick
   * Used: hovering over number cells in picker
   */
  playHover() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._osc(ctx, 'sine', 900, t, 0.04, 0.06)
  }

  /**
   * Number selected — satisfying "ping"
   * Used: clicking a number in the picker grid
   */
  playPick() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'sine', 660, 880, t, 0.05, 0.25)
    this._osc(ctx, 'sine', 1320, t + 0.04, 0.12, 0.12)
  }

  /**
   * Number locked in — "confirm" double-beep
   * Used: after successfully submitting number
   */
  playLockIn() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._osc(ctx, 'square', 523, t, 0.08, 0.22)       // C5
    this._osc(ctx, 'square', 784, t + 0.1, 0.12, 0.28) // G5
  }

  /**
   * Room created / joined — ascending chime
   * Used: after createRoom or joinRoom tx confirmed
   */
  playRoomJoin() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    const notes = [392, 523, 659, 784] // G4 C5 E5 G5
    notes.forEach((freq, i) => {
      this._osc(ctx, 'sine', freq, t + i * 0.09, 0.18, 0.22)
    })
  }

  /**
   * New round starts — retro arcade "level up" sweep
   * Used: when round number increments
   */
  playRoundStart() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'square', 220, 440, t, 0.12, 0.20)
    this._sweep(ctx, 'square', 330, 660, t + 0.08, 0.12, 0.14)
    this._osc(ctx, 'sine', 880, t + 0.18, 0.20, 0.18)
  }

  /**
   * Countdown tick — subtle metronome click
   * Used: last 10 seconds of round timer, every second
   */
  playCountdown() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._osc(ctx, 'square', 800, t, 0.055, 0.09)
  }

  /**
   * Countdown urgent — faster higher tick for last 5s
   * Used: last 5 seconds of round timer
   */
  playCountdownUrgent() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._osc(ctx, 'square', 1100, t, 0.055, 0.12)
  }

  /**
   * Elimination — ominous descending sawtooth crash
   * Used: when THIS player gets eliminated
   */
  playEliminate() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    // Low boom
    this._sweep(ctx, 'sawtooth', 200, 55, t, 0.6, 0.30)
    // Glitch noise layer
    this._sweep(ctx, 'square', 150, 40, t + 0.05, 0.5, 0.15)
    // High crackle
    this._sweep(ctx, 'sawtooth', 800, 200, t, 0.18, 0.12)
  }

  /**
   * Other player eliminated — shorter, quieter version
   * Used: when another player is eliminated (round resolve)
   */
  playOtherEliminated() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'sawtooth', 160, 60, t, 0.3, 0.18)
  }

  /**
   * Win fanfare — ascending major arpeggio with tail
   * Used: when THIS player wins the game
   */
  playWin() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    // Arpeggio: C E G C' E'
    const melody = [
      { freq: 523, dur: 0.20 },  // C5
      { freq: 659, dur: 0.20 },  // E5
      { freq: 784, dur: 0.20 },  // G5
      { freq: 1047, dur: 0.28 }, // C6
      { freq: 1319, dur: 0.40 }, // E6 — held
    ]
    melody.forEach((n, i) => {
      const offset = i * 0.13
      this._osc(ctx, 'sine', n.freq, t + offset, n.dur, 0.22)
      // Subtle square harmony one octave down
      this._osc(ctx, 'square', n.freq / 2, t + offset, n.dur * 0.6, 0.07)
    })
    // Final shimmer
    this._osc(ctx, 'sine', 2093, t + 0.70, 0.35, 0.10) // C7
  }

  /**
   * Tie / mutual destruction — dissonant pair of notes
   * Used: when game ends in a tie
   */
  playTie() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'sine', 440, 415, t, 0.5, 0.20) // A → Ab — slight detune
    this._sweep(ctx, 'sine', 554, 523, t, 0.5, 0.20) // C# → C
    this._sweep(ctx, 'sawtooth', 220, 110, t + 0.2, 0.5, 0.14)
  }

  /**
   * Notification / toast — soft two-tone alert
   * Used: toast messages
   */
  playNotification() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._osc(ctx, 'sine', 660, t, 0.10, 0.15)
    this._osc(ctx, 'sine', 880, t + 0.10, 0.12, 0.12)
  }

  /**
   * Error — low buzzer
   * Used: failed tx, error toast
   */
  playError() {
    if (!this.enabled) return
    const ctx = this._getCtx(); if (!ctx) return
    const t = ctx.currentTime
    this._sweep(ctx, 'sawtooth', 180, 100, t, 0.25, 0.22)
    this._osc(ctx, 'sawtooth', 80, t + 0.1, 0.20, 0.12)
  }

  // ─── CONTROL ───────────────────────────────────────────────

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol))
    if (this._masterGain) {
      this._masterGain.gain.setTargetAtTime(this.volume, this._getCtx()?.currentTime || 0, 0.01)
    }
  }

  toggle() {
    this.enabled = !this.enabled
    localStorage.setItem(STORAGE_KEY, String(this.enabled))
    return this.enabled
  }

  isEnabled() {
    return this.enabled
  }
}

export const soundManager = new SoundManager()
